import React, { useEffect, useReducer, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, Link, useNavigate } from "react-router-dom";
import { GoogleMap, useJsApiLoader, MarkerF } from "@react-google-maps/api";
import { MapPin, Clock, Eye, Heart, Store, CheckCircle2, ArrowLeft, Tag, FileText, ChevronLeft, ChevronRight, Building2, Navigation, QrCode, Flag, Languages, Loader2, Star, Undo2, LogIn, Globe, Copy, Leaf, X, ZoomIn } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api from "../api";
import { useFavourites } from "../context/FavouritesContext";
import { useFx } from "../context/FxContext";
import { useToast } from "../hooks/use-toast";
import Countdown from "../components/dashboard/Countdown";
import { ShareButton } from "../components/dashboard/ShareButton";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { FollowButton } from "../components/dashboard/FollowButton";
import { ReportDealDialog, NotHonouredButton } from "../components/dashboard/ReportDealDialog";
import { AdminDealActions } from "../components/dashboard/AdminDealActions";
import { PriceVerifiedBadge, PriceCheckPanel } from "../components/dashboard/PriceVerified";
import { QuietHoursBadge, QuietHoursLine } from "../components/dashboard/QuietHours";
import { money, symbolFor } from "../utils/currency";
import { directionsUrl } from "../utils/geo";
import { viewSource, plausibleEvent } from "../utils/track";
import { useSeo } from "../hooks/useSeo";
import { isOpenNow, dealOpenState, formatOpeningHours, isOnVacation } from "../utils/hours";
import { thumb } from "../utils/image";

const GKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const TRANSLATE_LANGS = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "pt", label: "Português" },
];

const OpenBadge = ({ state, testid }) => {
  if (!state || state === "unknown") return null;
  const map = {
    open: { t: "Open now", c: "bg-[#e7f6ec] text-[#16a34a]" },
    closed: { t: "Closed", c: "bg-[#fde8ee] text-[#e11d48]" },
    vacation: { t: "Temporarily closed", c: "bg-[#fdf2e0] text-[#b45309]" },
  };
  const m = map[state];
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${m.c}`} data-testid={testid}>{m.t}</span>;
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const MAX_SCALE = 4;

const Lightbox = ({ images, idx, setIdx, onClose, t }) => {
  const go = (d) => setIdx((i) => (i + d + images.length) % images.length);
  const imgRef = useRef(null);
  const st = useRef({ scale: 1, tx: 0, ty: 0 });
  const [, force] = useReducer((x) => x + 1, 0);
  const pointers = useRef(new Map());
  const g = useRef({ pinchDist: 0, pinchScale: 1, panStart: null, swipe: null, lastTap: 0, moved: false });

  const apply = (patch) => { st.current = { ...st.current, ...patch }; force(); };
  const reset = () => apply({ scale: 1, tx: 0, ty: 0 });

  useEffect(() => { reset(); }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (st.current.scale === 1 && e.key === "ArrowRight") go(1);
      else if (st.current.scale === 1 && e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clampPan = (scale, tx, ty) => {
    const el = imgRef.current;
    if (!el) return { tx, ty };
    const maxX = (el.offsetWidth * (scale - 1)) / 2;
    const maxY = (el.offsetHeight * (scale - 1)) / 2;
    return { tx: clamp(tx, -maxX, maxX), ty: clamp(ty, -maxY, maxY) };
  };

  // Zoom anchored at a client point so the spot under the finger/cursor stays put.
  const zoomTo = (nextScale, clientX, clientY) => {
    const el = imgRef.current;
    const s = clamp(nextScale, 1, MAX_SCALE);
    if (!el) { apply({ scale: s }); return; }
    if (s === 1) { apply({ scale: 1, tx: 0, ty: 0 }); return; }
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const { scale: cur, tx: ctx, ty: cty } = st.current;
    // offset of the anchor from image centre in unscaled px
    const ox = (clientX - cx - ctx) / cur;
    const oy = (clientY - cy - cty) / cur;
    const ntx = clientX - cx - ox * s;
    const nty = clientY - cy - oy * s;
    const c = clampPan(s, ntx, nty);
    apply({ scale: s, tx: c.tx, ty: c.ty });
  };

  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  const onPointerDown = (e) => {
    if (e.target.closest("button")) return; // let arrows/close handle themselves
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    g.current.moved = false;
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      g.current.pinchDist = dist(a, b);
      g.current.pinchScale = st.current.scale;
      g.current.panStart = null;
      g.current.swipe = null;
    } else if (pointers.current.size === 1) {
      if (st.current.scale > 1) {
        g.current.panStart = { x: e.clientX - st.current.tx, y: e.clientY - st.current.ty };
        g.current.swipe = null;
      } else {
        g.current.swipe = { x: e.clientX, y: e.clientY };
        g.current.panStart = null;
      }
      const now = Date.now();
      if (e.pointerType === "touch" && now - g.current.lastTap < 300) {
        g.current.lastTap = 0;
        if (st.current.scale > 1) reset();
        else zoomTo(2.5, e.clientX, e.clientY);
      } else {
        g.current.lastTap = now;
      }
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const d = dist(a, b);
      if (g.current.pinchDist > 0) {
        const m = mid(a, b);
        zoomTo(g.current.pinchScale * (d / g.current.pinchDist), m.x, m.y);
        g.current.moved = true;
      }
    } else if (pointers.current.size === 1 && st.current.scale > 1 && g.current.panStart) {
      const c = clampPan(st.current.scale, e.clientX - g.current.panStart.x, e.clientY - g.current.panStart.y);
      apply({ tx: c.tx, ty: c.ty });
      g.current.moved = true;
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) g.current.pinchDist = 0;
    // Only handle swipe/dismiss gestures at 1x with a single finger that didn't pinch/pan.
    if (st.current.scale === 1 && g.current.swipe && !g.current.moved && pointers.current.size === 0) {
      const dx = e.clientX - g.current.swipe.x;
      const dy = e.clientY - g.current.swipe.y;
      if (Math.abs(dy) > 70 && Math.abs(dy) > Math.abs(dx)) onClose();
      else if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
      else if (!e.target.closest("button") && !e.target.closest("img")) onClose(); // tap on backdrop
    }
    g.current.swipe = null;
    g.current.panStart = null;
  };

  const onWheel = (e) => {
    zoomTo(st.current.scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15), e.clientX, e.clientY);
  };

  const onDoubleClick = (e) => {
    if (e.target.closest("button")) return;
    if (st.current.scale > 1) reset();
    else zoomTo(2.5, e.clientX, e.clientY);
  };

  const { scale, tx, ty } = st.current;
  const zoomed = scale > 1;
  return (
    <div data-testid="deal-gallery-lightbox" role="dialog" aria-modal="true" aria-label={t("deal.zoom") || "Deal image"}
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center animate-in fade-in duration-150 touch-none overscroll-none select-none"
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onWheel={onWheel} onDoubleClick={onDoubleClick}>
      <button type="button" onClick={onClose} data-testid="deal-gallery-lightbox-close"
        style={{ top: "max(0.75rem, env(safe-area-inset-top))", right: "max(0.75rem, env(safe-area-inset-right))" }}
        className="absolute z-10 h-12 w-12 rounded-full bg-white/15 hover:bg-white/25 active:bg-white/30 text-white flex items-center justify-center transition-colors" aria-label={t("common.close") || "Close"}>
        <X size={24} />
      </button>
      <img ref={imgRef} src={thumb(images[idx], 1600)} alt={`photo ${idx + 1}`} draggable="false"
        style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})`, cursor: zoomed ? "grab" : "zoom-in" }}
        className="max-w-[94vw] max-h-[88dvh] sm:max-h-[90dvh] object-contain select-none will-change-transform" data-testid="deal-gallery-lightbox-img" />
      {images.length > 1 && !zoomed && (
        <>
          <button type="button" onClick={() => go(-1)} aria-label="Previous"
            className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/15 hover:bg-white/25 text-white items-center justify-center transition-colors"><ChevronLeft size={26} /></button>
          <button type="button" onClick={() => go(1)} aria-label="Next"
            className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/15 hover:bg-white/25 text-white items-center justify-center transition-colors"><ChevronRight size={26} /></button>
          <span style={{ bottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
            className="absolute left-1/2 -translate-x-1/2 bg-white/15 text-white text-xs font-bold px-3 py-1 rounded-full">{idx + 1} / {images.length}</span>
        </>
      )}
      <span style={{ bottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
        className={`absolute right-4 text-white/60 text-[11px] transition-opacity ${zoomed ? "opacity-0" : "opacity-100"}`}>
        {t("deal.zoomHint") || "Pinch or double-tap to zoom"}
      </span>
      {zoomed && (
        <span data-testid="deal-gallery-zoom-level" style={{ top: "max(0.75rem, env(safe-area-inset-top))" }}
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 bg-white/15 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1.5 animate-in fade-in duration-150">
          <ZoomIn size={13} /> {Math.round(scale * 100)}% · {t("deal.zoomReset") || "Double-tap to reset"}
        </span>
      )}
    </div>
  );
};

const Gallery = ({ images, discount, featured }) => {
  const { t } = useLang();
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const touchX = useRef(null);
  const go = (d) => setIdx((i) => (i + d + images.length) % images.length);
  return (
    <div data-testid="deal-gallery" className="min-w-0">
      <div className="relative rounded-2xl overflow-hidden border border-border"
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
          touchX.current = null;
        }}>
        <button type="button" onClick={() => setZoom(true)} data-testid="deal-gallery-zoom" aria-label={t("deal.zoom") || "View full image"}
          className="relative block w-full h-72 lg:h-[420px] bg-secondary cursor-zoom-in">
          <img src={thumb(images[idx], 1200)} alt="" aria-hidden="true" decoding="async" className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-50" />
          <img src={thumb(images[idx], 1200)} alt={`photo ${idx + 1}`} decoding="async" className="relative w-full h-full object-contain transition-all" data-testid="deal-gallery-main" />
        </button>
        <span className="absolute top-4 left-4 bg-[#16a34a] text-white text-sm font-bold px-3 py-1.5 rounded-lg">{t("common.percentOff", { n: discount })}</span>
        {featured && <span className="absolute top-4 right-4 bg-rose-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg">{t("deal.featured")}</span>}
        {images.length > 1 && (
          <>
            <button onClick={() => go(-1)} data-testid="deal-gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/85 hover:bg-white flex items-center justify-center shadow text-slate-700 transition-colors" aria-label="Previous"><ChevronLeft size={20} /></button>
            <button onClick={() => go(1)} data-testid="deal-gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/85 hover:bg-white flex items-center justify-center shadow text-slate-700 transition-colors" aria-label="Next"><ChevronRight size={20} /></button>
            <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-bold px-2.5 py-1 rounded-full" data-testid="deal-gallery-counter">{idx + 1} / {images.length}</span>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-2 bg-white/60"}`} aria-label={`Photo ${i + 1}`} />
              ))}
            </div>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto lp-scroll pb-1 -mx-1 px-1" data-testid="deal-gallery-thumbs">
          {images.map((src, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`deal-gallery-thumb-${i}`}
              className={`shrink-0 h-16 w-20 rounded-xl overflow-hidden border-2 transition-all ${i === idx ? "border-[#16a34a] shadow-md" : "border-border opacity-70 hover:opacity-100"}`}>
              <img src={thumb(src, 160)} alt={`thumbnail ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {zoom && createPortal(
        <Lightbox images={images} idx={idx} setIdx={setIdx} onClose={() => setZoom(false)} t={t} />,
        document.body
      )}
    </div>
  );
};

const DealDetail = () => {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isFav, toggleFav } = useFavourites();
  const { user } = useAuth();
  const { approx, convert } = useFx();
  const { toast } = useToast();
  const { isLoaded } = useJsApiLoader({ id: "gmap-script", googleMapsApiKey: GKEY || "" });
  const [deal, setDeal] = useState(null);
  const [grabbed, setGrabbed] = useState(false);
  const [code, setCode] = useState(null);
  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transLang, setTransLang] = useState("en");
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    setLoading(true);
    setGrabbed(false);
    setCode(null);
    setPromo(null);
    setTransLang("en");
    setTranslation(null);
    const src = viewSource(new URLSearchParams(window.location.search).get("src"));
    api.get(`/deals/${id}`, { params: { src } }).then((r) => setDeal(r.data)).catch(() => setDeal(null)).finally(() => setLoading(false));
  }, [id]);

  useSeo(deal ? {
    title: deal.discount > 0 ? `${deal.discount}% off ${deal.title} — ${deal.business}` : `${deal.title} — ${deal.business}`,
    description: (deal.description || "").slice(0, 200) || `${deal.discount > 0 ? `${deal.discount}% off` : "Deal"} at ${deal.business}${deal.location ? `, ${deal.location}` : ""}. Grab it on LytePress.`,
    image: deal.image,
  } : {});

  const changeLang = async (lang) => {    setTransLang(lang);
    if (lang === "en") { setTranslation(null); return; }
    setTranslating(true);
    try {
      const { data } = await api.post(`/deals/${id}/translate`, { lang });
      setTranslation(data.translation);
    } catch (err) {
      setTransLang("en");
      setTranslation(null);
      toast({ title: t("deal.translateFail"), description: t("common.tryAgain") });
    } finally {
      setTranslating(false);
    }
  };

  const savedToast = (title) => toast({
    title, description: <span>{t("dd.savedTo")} <Link to="/my-deals" className="font-bold underline" data-testid="toast-my-deals-link">{t("dd.myDeals")}</Link> — show it at the business to redeem.</span>,
  });

  const grab = async () => {
    if (!user) {
      localStorage.setItem("lp-post-login-grab", id);
      toast({ title: t("deal.signInToGrab"), description: t("deal.signInToGrabMsg") });
      navigate("/login");
      return;
    }
    try {
      if (deal.codeRequired) {
        const { data } = await api.post(`/deals/${id}/code`);
        setCode(data.code);
        setGrabbed(true);
        savedToast("Your code is ready");
      } else {
        const { data } = await api.post(`/deals/${id}/claim`);
        if (data.promoCode) setPromo(data.promoCode);
        setGrabbed(true);
        savedToast(data.promoCode ? "Your promo code is ready" : "Deal grabbed!");
      }
      plausibleEvent("Deal Grab", { category: deal.category || "" });
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.setItem("lp-post-login-grab", id);
        toast({ title: t("deal.signInToGrab"), description: t("deal.signInToGrabMsg") });
        navigate("/login");
      } else {
        toast({ title: t("deal.couldNotGrab"), description: t("common.tryAgain") });
      }
    }
  };

  // Auto-complete a grab that was interrupted by login/signup, else restore an existing grab
  useEffect(() => {
    if (!deal || !user || user.role !== "shopper") return;
    if (localStorage.getItem("lp-post-login-grab") === id) {
      localStorage.removeItem("lp-post-login-grab");
      grab();
      return;
    }
    api.get("/my/grabs").then((r) => {
      const mine = (r.data.items || []).find((it) => it.dealId === id && it.status !== "redeemed");
      if (mine) { setGrabbed(true); setCode(mine.code || null); setPromo(mine.promoCode || null); }
    }).catch(() => {});
  }, [deal, user]); // eslint-disable-line

  const copyPromo = async () => {
    try {
      await navigator.clipboard.writeText(promo);
      toast({ title: t("deal.codeCopied"), description: t("deal.pasteAt", { code: promo }) });
    } catch {
      toast({ title: t("mydeals.yourCode"), description: promo });
    }
  };

  const undo = async () => {    try {
      if (deal.codeRequired) await api.delete(`/deals/${id}/code`);
      else await api.post(`/deals/${id}/unclaim`);
      setGrabbed(false);
      setCode(null);
      toast({ title: t("deal.grabUndone"), description: t("deal.grabUndoneMsg") });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast({ title: t("mydeals.undoFailed"), description: typeof d === "string" ? d : t("common.tryAgain") });
    }
  };

  if (loading) return <div className="h-96 rounded-2xl bg-secondary animate-pulse" />;  if (!deal) return (
    <div className="text-center py-20">
      <p className="text-muted-foreground mb-4">{t("deal.notFound")}</p>
      <Link to="/browse" className="text-[#16a34a] font-semibold">{t("deal.back")}</Link>
    </div>
  );

  const fav = isFav(deal.id);
  const dTitle = (transLang !== "en" && translation?.title) || deal.title;
  const dDescription = (transLang !== "en" && translation?.description) || deal.description;
  const dTerms = (transLang !== "en" && translation?.terms) || deal.terms;
  const images = (deal.images && deal.images.length ? deal.images : [deal.image]).filter(Boolean);
  const branches = (deal.branches && deal.branches.length ? deal.branches : (deal.lat != null ? [{ label: deal.location, address: deal.location, lat: deal.lat, lng: deal.lng }] : [])).filter((b) => b.lat != null);
  const fitBranches = (map) => {
    if (!window.google || !branches.length) return;
    if (branches.length === 1) { map.setCenter({ lat: branches[0].lat, lng: branches[0].lng }); map.setZoom(14); return; }
    const bounds = new window.google.maps.LatLngBounds();
    branches.forEach((b) => bounds.extend({ lat: b.lat, lng: b.lng }));
    map.fitBounds(bounds, 60);
  };

  return (
    <div className="space-y-6 lp-fade-up max-w-5xl">
      <Link to="/browse" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> {t("dd.backToDeals")}</Link>

      <div className="grid lg:grid-cols-2 gap-6 min-w-0">
        <Gallery images={images} discount={deal.discount} featured={deal.featured} />

        <div className="space-y-4 min-w-0">
          <div className="min-w-0">
            <h1 className="text-2xl font-extrabold text-foreground break-words" data-testid="deal-title">{dTitle}</h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-1.5">
              <Link to={`/business/${encodeURIComponent(deal.business)}`} className="flex items-center gap-2 text-muted-foreground hover:text-[#16a34a] transition-colors" data-testid="deal-business-link"><Store size={16} className="text-[#16a34a]" /> {deal.business}</Link>
              {deal.businessRating != null && (
                <Link to={`/business/${encodeURIComponent(deal.business)}#reviews`} data-testid="deal-business-rating" className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-bold text-foreground hover:text-[#16a34a] transition-colors">
                  <Star size={13} className="fill-[#f5b942] text-[#f5b942]" /> {deal.businessRating} <span className="font-medium text-muted-foreground">· {t(deal.businessReviewCount === 1 ? "card.review" : "card.reviews", { n: deal.businessReviewCount })}</span>
                </Link>
              )}
              <FollowButton business={deal.business} size="sm" />
            </div>
            <div className="mt-3 hidden items-center gap-2 rounded-xl border border-border bg-secondary/60 px-2.5 py-1.5">
              <Languages size={16} className="text-[#16a34a]" />
              <span className="text-xs font-semibold text-muted-foreground">{t("deal.translate")}</span>
              <select
                data-testid="translate-lang-select"
                value={transLang}
                onChange={(e) => changeLang(e.target.value)}
                disabled={translating}
                className="bg-transparent text-sm font-semibold text-foreground focus:outline-none cursor-pointer disabled:opacity-60"
              >
                {TRANSLATE_LANGS.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
              {translating && <Loader2 size={14} className="animate-spin text-[#16a34a]" data-testid="translate-spinner" />}
              {transLang !== "en" && !translating && <span className="text-[10px] font-bold text-[#16a34a] bg-[#e7f6ec] px-1.5 py-0.5 rounded" data-testid="translated-badge">{t("deal.translated")}</span>}
            </div>
          </div>

          <div className="flex items-center gap-2" data-testid="deal-action-bar">
            <ShareButton deal={deal} menu label iconSize={18}
              className="flex-1 sm:flex-none h-11 px-4 inline-flex items-center justify-center gap-2 rounded-xl border border-border font-bold text-sm text-foreground hover:text-[#16a34a] hover:border-[#16a34a] transition-colors" />
            <button onClick={() => toggleFav(deal.id)} data-testid="deal-fav-btn"
              className={`flex-1 sm:flex-none h-11 px-4 inline-flex items-center justify-center gap-2 rounded-xl border font-bold text-sm transition-colors ${fav ? "border-rose-400 text-rose-500" : "border-border text-foreground hover:border-rose-400"}`} aria-label={t("dd.favourite")}>
              <Heart size={18} className={fav ? "fill-rose-500 text-rose-500" : ""} />
              <span>{fav ? t("dd.saved") : t("dd.save")}</span>
            </button>
            <ReportDealDialog dealId={deal.id} />
          </div>

          <AdminDealActions deal={deal} variant="page"
            onArchived={() => setDeal((d) => ({ ...d, hidden: true }))}
            onRestored={() => setDeal((d) => ({ ...d, hidden: false }))}
            onDeleted={() => navigate("/browse")} />

          {deal.price != null && (
            <div className="flex flex-wrap items-end gap-x-3 gap-y-2 min-w-0">
              <span className="text-2xl sm:text-3xl font-extrabold text-[#16a34a] break-all">{money(deal.price, deal.currency)}</span>
              {deal.was != null && <span className="text-lg text-muted-foreground line-through mb-0.5 break-all">{money(deal.was, deal.currency)}</span>}
              {deal.save != null && <span className="mb-1 bg-[#e7f6ec] text-[#16a34a] text-sm font-bold px-2.5 py-1 rounded-lg">{t("card.save", { amount: `${symbolFor(deal.currency)}${deal.save}` })}</span>}
            </div>
          )}
          {deal.price != null && approx(deal.price, deal.currency) && (
            <div className="-mt-1">
              <span data-testid="deal-detail-approx" className="inline-flex items-center gap-1.5 rounded-full bg-[#e7f6ec] dark:bg-[#16341f] border border-[#16a34a]/30 text-[#16a34a] font-bold text-sm px-3 py-1">
                {approx(deal.price, deal.currency)} <span className="font-medium opacity-80">{t("deal.inYourCurrency")}</span>
              </span>
            </div>
          )}

          <PriceCheckPanel deal={deal} onUpdate={(d) => setDeal((prev) => ({ ...prev, ...d }))} />

          {deal.recurring && (
            <div className="rounded-2xl border border-border bg-secondary/50 p-4 space-y-1" data-testid="quiet-hours-block">
              <QuietHoursBadge deal={deal} />
              <QuietHoursLine deal={deal} />
            </div>
          )}

          <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-sm">
            <span className="flex items-center gap-1.5 text-orange-500 font-medium"><Clock size={16} /> <Countdown expiresAt={deal.expiresAt} fallback={deal.timeLeft} /></span>
            {deal.isOnline ? (
              <span className="flex items-center gap-1.5 font-semibold text-[#16a34a]" data-testid="deal-online-badge"><Globe size={16} /> {t("deal.onlineBadge")}</span>
            ) : (
              <span className="flex items-center gap-1.5 text-muted-foreground min-w-0"><MapPin size={16} className="text-[#16a34a] shrink-0" /> <span className="break-words min-w-0">{deal.location}{deal.country ? `, ${deal.country}` : ""}</span></span>
            )}
            <span className="flex items-center gap-1.5 text-muted-foreground"><Eye size={16} /> {t("deal.views", { n: deal.views })}</span>
          </div>

          <p className="text-muted-foreground leading-relaxed" data-testid="deal-description">{dDescription}</p>

          {deal.shortDated && (
            <div className="rounded-xl border border-[#16a34a]/40 bg-[#e7f6ec] dark:bg-[#16341f] px-4 py-3 flex items-start gap-2.5" data-testid="short-dated-note">
              <Leaf size={16} className="text-[#16a34a] shrink-0 mt-0.5" />
              <p className="text-sm text-foreground">
                <span className="font-bold">{t("deal.shortDatedTitle")}</span>
                {deal.bestBefore ? ` — ${t("deal.shortDatedUseBy", { date: new Date(deal.bestBefore).toLocaleDateString() })}.` : "."}
                <span className="text-muted-foreground"> {t("deal.shortDatedNote")}</span>
              </p>
            </div>
          )}

          {grabbed && code ? (
            <div className="rounded-2xl border border-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] p-5 text-center" data-testid="redemption-code-panel">
              <p className="flex items-center justify-center gap-2 text-[#16a34a] font-bold"><CheckCircle2 size={18} /> {t("dd.showThisAtCheckout")}</p>
              <div className="mt-3 bg-white rounded-xl p-4 inline-block"><QRCodeSVG value={code} size={140} /></div>
              <p className="mt-3 text-3xl font-extrabold tracking-[0.3em] text-foreground" data-testid="redemption-code">{code}</p>
              <p className="text-xs text-muted-foreground mt-2">{t("deal.staffCode", { business: deal.business })} <Link to="/my-deals" className="font-semibold text-[#16a34a] hover:underline">{t("deal.myDeals")}</Link>.</p>
              <button onClick={undo} data-testid="undo-grab-btn" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-rose-500 transition-colors">
                <Undo2 size={13} /> {t("dd.changedYourMindUndo")}
              </button>
            </div>
          ) : grabbed ? (
            <div className="rounded-2xl border border-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] p-5">
              <p className="flex items-center gap-2 text-[#16a34a] font-bold"><CheckCircle2 size={18} /> {t("deal.grabbed")}</p>
              {deal.isOnline && promo ? (
                <div className="mt-3" data-testid="promo-code-panel">
                  <p className="text-sm text-muted-foreground">{t("deal.promoUse", { business: deal.business })}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-2xl font-extrabold tracking-[0.2em] text-foreground bg-card border border-[#16a34a]/40 rounded-xl px-4 py-2" data-testid="promo-code-value">{promo}</span>
                    <button onClick={copyPromo} data-testid="copy-promo-btn" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-colors"><Copy size={15} /> {t("deal.copyCode")}</button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{t("deal.savedInPre")} <Link to="/my-deals" className="font-semibold text-[#16a34a] hover:underline" data-testid="grabbed-my-deals-link">{t("deal.myDeals")}</Link> {t("deal.savedUntilExpiry")}</p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mt-1">{t("deal.visitToRedeem", { business: deal.business, location: deal.location })} {t("deal.savedInPre")} <Link to="/my-deals" className="font-semibold text-[#16a34a] hover:underline" data-testid="grabbed-my-deals-link">{t("deal.myDeals")}</Link>.</p>
              )}
              <button onClick={undo} data-testid="undo-grab-btn" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-rose-500 transition-colors">
                <Undo2 size={13} /> {t("dd.changedYourMindUndo")}
              </button>
            </div>
          ) : (
            <button onClick={grab} data-testid="grab-deal-btn" className="w-full bg-[#16a34a] hover:bg-[#15803d] text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2">
              {!user ? <><LogIn size={18} /> {t("deal.signInGrab")}</> : deal.codeRequired ? <><QrCode size={18} /> {t("deal.getCode")}</> : deal.isOnline ? <><Globe size={18} /> {t("deal.getPromo")}</> : <><Tag size={18} /> {t("deal.grab")}</>}
            </button>
          )}
          {!user && deal.codeRequired && !code && !grabbed && (
            <p className="text-center text-xs text-muted-foreground -mt-1" data-testid="grab-login-hint">{t("deal.loginHint")}</p>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
          <h3 className="font-bold text-foreground flex items-center gap-2"><FileText size={18} className="text-[#16a34a]" /> {t("deal.terms")}</h3>
          <p className="text-sm text-muted-foreground" data-testid="deal-terms">{dTerms || "Valid while stocks last. Cannot be combined with other offers. Present this deal in-store to redeem."}</p>
          <div className="pt-1">
            <NotHonouredButton dealId={deal.id} className="w-full sm:w-auto" />
            <p className="text-xs text-muted-foreground mt-2">{t("rpt.notHonouredHelp")}</p>
          </div>
        </div>

        {isOnVacation(deal.vacation) && (
          <div className="rounded-2xl border border-[#f5b942]/40 bg-[#fdf2e0] dark:bg-[#3a2f10] p-4 flex items-start gap-3" data-testid="vacation-banner">
            <Clock size={18} className="text-[#b45309] shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-[#b45309]">Temporarily closed{deal.vacation.until ? ` — reopening ${new Date(deal.vacation.until).toLocaleDateString()}` : ""}</p>
              {deal.vacation.message && <p className="text-sm text-[#7c5010] dark:text-[#f5b942]/90 mt-0.5">{deal.vacation.message}</p>}
            </div>
          </div>
        )}

        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {deal.isOnline ? (
            <div className="p-5 space-y-2" data-testid="online-deal-card">
              <h3 className="font-bold text-foreground flex items-center gap-2"><Globe size={18} className="text-[#16a34a]" /> {t("deal.onlineTitle")}</h3>
              <p className="text-sm text-muted-foreground">No store visit needed — grab the deal to reveal your promo code, then use it at {deal.business}'s online checkout.</p>
              <p className="text-xs text-muted-foreground">{t("deal.onlineWorldwide")}</p>
            </div>
          ) : (
          <>
          <div className="p-5 pb-3">
            <h3 className="font-bold text-foreground flex items-center gap-2"><MapPin size={18} className="text-[#16a34a]" /> {branches.length > 1 ? t("deal.availableAt", { n: branches.length }) : t("deal.location")}</h3>
            {branches.length > 1 ? (
              <ul className="mt-2 space-y-2.5" data-testid="deal-branch-list">
                {branches.map((b, i) => {
                  const state = dealOpenState(deal, b);
                  const dir = directionsUrl(b.lat, b.lng);
                  const hoursText = formatOpeningHours(deal.openingHours) || b.hours || deal.hours;
                  const bcur = b.currency && b.currency !== deal.currency ? b.currency : null;
                  let priceStr = null, wasStr = null, manual = false, uApprox = null;
                  if (bcur && deal.price != null) {
                    if (b.price != null) { priceStr = `${symbolFor(bcur)}${b.price}`; wasStr = b.was != null ? `${symbolFor(bcur)}${b.was}` : null; manual = true; uApprox = approx(b.price, bcur); }
                    else { priceStr = convert(deal.price, deal.currency, bcur); wasStr = deal.was != null ? convert(deal.was, deal.currency, bcur) : null; uApprox = approx(deal.price, deal.currency); }
                  }
                  return (
                    <li key={i} className="text-sm">
                      <div className="flex items-center gap-2 text-foreground font-medium">
                        <Building2 size={14} className="text-[#16a34a] shrink-0" /> {b.address || b.label || `Branch ${i + 1}`}
                        <OpenBadge state={state} testid={`branch-open-${i}`} />
                        {bcur && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground" data-testid={`branch-currency-${i}`}>{bcur}</span>
                        )}
                        {dir && (
                          <a href={dir} target="_blank" rel="noopener noreferrer" onClick={() => api.post(`/deals/${id}/directions`, null, { params: { src: "deal" } }).catch(() => {})} data-testid={`branch-directions-${i}`} className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-[#16a34a] hover:underline shrink-0"><Navigation size={12} /> Directions</a>
                        )}
                      </div>
                      {priceStr && (
                        <p className="text-xs ml-6 mt-0.5 flex flex-wrap items-center gap-2" data-testid={`branch-price-display-${i}`}>
                          <span className="font-bold text-[#16a34a]">{priceStr}</span>
                          {wasStr && <span className="text-muted-foreground line-through">{wasStr}</span>}
                          <span className="text-[10px] text-muted-foreground">{manual ? "local price" : "≈ converted"}</span>
                          {uApprox && <span className="text-[11px] font-bold text-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] border border-[#16a34a]/30 px-2 py-0.5 rounded-full" data-testid={`branch-price-approx-${i}`}>{uApprox} in your currency</span>}
                        </p>
                      )}
                      {hoursText && <p className="text-xs text-muted-foreground ml-6 flex items-center gap-1.5"><Clock size={12} /> {hoursText}</p>}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="mt-1">
                <p className="text-sm text-muted-foreground" data-testid="deal-location-text">{(branches[0]?.address || branches[0]?.label || deal.location)}{deal.country ? `, ${deal.country}` : ""}</p>
                {(() => {
                  const b = branches[0] || null;
                  const state = dealOpenState(deal, b);
                  const hoursText = formatOpeningHours(deal.openingHours) || (b && b.hours) || deal.hours;
                  return (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                      <Clock size={12} /> {hoursText}
                      <OpenBadge state={state} testid="branch-open-0" />
                    </p>
                  );
                })()}
                {branches[0] && directionsUrl(branches[0].lat, branches[0].lng) && (
                  <a href={directionsUrl(branches[0].lat, branches[0].lng)} target="_blank" rel="noopener noreferrer" onClick={() => api.post(`/deals/${id}/directions`, null, { params: { src: "deal" } }).catch(() => {})} data-testid="deal-directions-btn" className="mt-3 inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"><Navigation size={16} /> {t("deal.directions")}</a>
                )}
              </div>
            )}
          </div>
          <div className="h-56">
            {GKEY && isLoaded && branches.length ? (
              <GoogleMap mapContainerStyle={{ width: "100%", height: "100%" }} onLoad={fitBranches} options={{ disableDefaultUI: true }}>
                {branches.map((b, i) => (
                  <MarkerF key={i} position={{ lat: b.lat, lng: b.lng }} label={branches.length > 1 ? { text: String(i + 1), color: "#fff", fontWeight: "700" } : undefined} />
                ))}
              </GoogleMap>
            ) : (
              <div className="h-full flex items-center justify-center bg-secondary text-muted-foreground text-sm">{t("deal.mapUnavailable")}</div>
            )}
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealDetail;
